import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  GoogleMap,
  useLoadScript,
  Autocomplete,
  OverlayViewF,
} from "@react-google-maps/api";
import {
  Address,
  Container,
  Description,
  Load,
  Name,
  Photo,
  Redes,
  RedesImage,
  SearchContainer,
  TotalPartners,
  ViewInput,
  WarningTag,
} from "./styles";
import api from "../services/api";
import { toast } from "react-toastify";
import { Button, Input, Modal, Spin } from "antd";
import { redesSociais } from "../Const";
import { MdOutlineFilterList } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import defaultUserImage from "../images/user.png";

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const DEFAULT_CATEGORY_COLOR = "#20D072";

const SYSTEM_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Helper para converter hex para rgba
function hexToRgba(hex, alpha) {
  const fallback = hex || DEFAULT_CATEGORY_COLOR;
  const cleanHex = fallback.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16) || 0;
  const g = parseInt(cleanHex.substring(2, 4), 16) || 0;
  const b = parseInt(cleanHex.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}



// ─── Styled Components do Pin ─────────────────────────────────────────────────

const Tooltip = styled.div`
  visibility: hidden;
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translate(-50%, 6px);
  background: #fff;
  color: #222;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-family: ${SYSTEM_FONT};
  width: max-content;
  max-width: 290px;
  min-width: 170px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 99999;
  opacity: 0;
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 6px;
  border-left: 4px solid ${(props) => props.$color || DEFAULT_CATEGORY_COLOR};

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -7px;
    border-width: 7px;
    border-style: solid;
    border-color: #fff transparent transparent transparent;
  }
`;

const PinContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s ease;
  will-change: transform;
  position: relative;
  overflow: visible;

  &:hover {
    transform: scale(1.18) translateY(-2px);
    z-index: 99999;
  }

  &:hover ${Tooltip} {
    visibility: visible;
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;

const PinBubble = styled.div`
  width: ${(props) => props.$size || 48}px;
  height: ${(props) => props.$size || 48}px;
  border-radius: 50%;
  border: 3px solid ${(props) => props.$color || DEFAULT_CATEGORY_COLOR};
  background-color: #fff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: box-shadow 0.2s ease;

  ${PinContainer}:hover & {
    box-shadow: 0 0 0 3px ${(props) =>
      hexToRgba(props.$color || DEFAULT_CATEGORY_COLOR, 0.3)},
      0 4px 14px rgba(0, 0, 0, 0.3);
  }
`;

const PinImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PinTail = styled.div`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid ${(props) => props.$color || DEFAULT_CATEGORY_COLOR};
  margin-top: -2px;
`;

// ─── Styled Components dos Filtros ────────────────────────────────────────────

const CategoryChip = styled.div`
  padding: 6px 14px;
  border-radius: 50px;
  cursor: pointer;
  font-size: 12px;
  font-family: ${SYSTEM_FONT};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  user-select: none;
  white-space: nowrap;

  ${(props) => {
    const color = props.$color || DEFAULT_CATEGORY_COLOR;
    if (props.$selected) {
      return css`
        background-color: ${color};
        color: #fff;
        box-shadow: 0 2px 10px ${hexToRgba(color, 0.3)};

        &:hover {
          box-shadow: 0 3px 14px ${hexToRgba(color, 0.4)};
        }

        &:active {
          transform: scale(0.97);
        }
      `;
    }
    return css`
      background-color: #f5f5f5;
      color: #555;
      border: 1.5px solid #e0e0e0;

      &:hover {
        background-color: ${hexToRgba(color, 0.08)};
        border-color: ${hexToRgba(color, 0.4)};
        color: ${color};
      }

      &:active {
        transform: scale(0.97);
      }
    `;
  }}
`;

const CategoryDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.$color || DEFAULT_CATEGORY_COLOR};
  flex-shrink: 0;
`;

const FilterModalContent = styled.div`
  .filter-section {
    margin-bottom: 20px;
  }

  .filter-label {
    font-size: 13px;
    font-family: ${SYSTEM_FONT};
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
    padding: 4px 2px;
  }

  .active-filters-count {
    background: ${DEFAULT_CATEGORY_COLOR};
    color: #fff;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    font-family: ${SYSTEM_FONT};
  }
`;

const TooltipCategoryBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-family: ${SYSTEM_FONT};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background-color: ${(props) =>
    hexToRgba(props.$color || DEFAULT_CATEGORY_COLOR, 0.12)};
  color: ${(props) => props.$color || DEFAULT_CATEGORY_COLOR};
  width: fit-content;
`;

// ─── Componente PartnerMarker Memoizado ───────────────────────────────────────

const PartnerMarker = React.memo(
  ({ marker, onClick, defaultUserImage, categoryColor, pinSize }) => {
    const handleClick = useCallback(() => {
      onClick(marker);
    }, [marker, onClick]);

    const color = categoryColor || DEFAULT_CATEGORY_COLOR;

    return (
      <OverlayViewF
        position={{ lat: marker.latitude, lng: marker.longitude }}
        mapPaneName="floatPane"
        getPixelPositionOffset={(width, height) => ({
          x: -(width / 2),
          y: -height,
        })}
      >
        <PinContainer onClick={handleClick}>
          <Tooltip $color={color}>
            <strong
              style={{
                display: "block",
                fontSize: 14,
                color: "#111",
                fontFamily: SYSTEM_FONT,
                fontWeight: 700,
                lineHeight: 1.3,
              }}
            >
              {marker.label}
            </strong>
            {marker.category_name && (
              <TooltipCategoryBadge $color={color}>
                {marker.category_name}
              </TooltipCategoryBadge>
            )}
            {marker.description && (
              <span
                style={{
                  display: "block",
                  fontSize: 12,
                  color: "#555",
                  fontFamily: SYSTEM_FONT,
                  fontWeight: 400,
                  whiteSpace: "normal",
                  lineHeight: 1.5,
                }}
              >
                {marker.description.length > 80
                  ? marker.description.substring(0, 80) + "…"
                  : marker.description}
              </span>
            )}
            {(marker.street || marker.neighborhood) && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  color: "#888",
                  fontFamily: SYSTEM_FONT,
                  fontWeight: 500,
                  lineHeight: 1.4,
                  marginTop: 2,
                  paddingTop: 5,
                  borderTop: "1px solid #eee",
                }}
              >
                <span style={{ flexShrink: 0 }}>📍</span>
                <span>
                  {marker.street &&
                    `${marker.street}${
                      marker.number ? `, ${marker.number}` : ""
                    }`}
                  {marker.neighborhood &&
                    `${marker.street ? " - " : ""}${marker.neighborhood}`}
                  {marker.city && ` · ${marker.city}`}
                </span>
              </span>
            )}
          </Tooltip>
          <PinBubble $color={color} $size={pinSize}>
            <PinImage
              src={marker.photo_url || defaultUserImage}
              alt={marker.label}
              loading="lazy"
              onError={(e) => {
                e.target.src = defaultUserImage;
              }}
            />
          </PinBubble>
          <PinTail $color={color} />
        </PinContainer>
      </OverlayViewF>
    );
  }
);

// ─── Componente Principal ─────────────────────────────────────────────────────

export default function Mappss() {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyD8zV3OvIQtzv2Lv_kEE9zMWPirD8tzpTM", // Insira sua chave de API do Google Maps aqui
    libraries,
  });

  const [searchParams] = useSearchParams();

  // Pega valores da query string
  const keywordsQ = searchParams.get("keywords");
  const categoriesQ = searchParams.get("categories");
  const categoriesFilterQ = searchParams.get("categoriesFilter");
  const filterQ = searchParams.get("filter");
  const location = searchParams.get("location");
  const latitude = searchParams.get("latitude");
  const longitude = searchParams.get("longitude");
  const zoomC = searchParams.get("zoom");

  const mapRef = useRef(null);

  const [regiao, setRegiao] = useState({
    lat: -19.919052,
    lng: -46.633308,
  });
  const [regiaoMax, setRegiaoMax] = useState(null);
  const [zoom, setZoom] = useState(zoomC ? Number(zoomC) : 14);
  const [autocomplete, setAutocomplete] = useState(null);

  // Estado para o modal de filtros
  const [visibleModalFilter, setVisibleModalFilter] = useState(false);
  const [visibleModal, setVisibleModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [keywordsSearch, setKeywordsSearch] = useState(keywordsQ || "");
  const [keywords, setKeywords] = useState("");
  const [categoriesSearch, setCategoriesSearch] = useState(
    categoriesQ ? categoriesQ.split(",") : []
  );
  const [categoriesFilter, setCategoriesFilter] = useState([]);

  const [partner, setPartner] = useState({});
  const [partners, setPartners] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  // Mapa de cores por categoria_id para acesso rápido
  const categoryColorMap = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      map[String(cat.id)] = cat.color || DEFAULT_CATEGORY_COLOR;
    });
    return map;
  }, [categories]);

  const handleMarkerClick = useCallback((marker) => {
    setPartner(marker);
    setVisibleModal(true);
  }, []);

  // Tamanho dinâmico do pin baseado no zoom
  const pinSize = useMemo(() => {
    if (zoom >= 18) return 56;
    if (zoom >= 16) return 50;
    if (zoom >= 14) return 46;
    return 40;
  }, [zoom]);



  useEffect(() => {
    if (location == "false") {
      if (latitude && longitude) {
        setRegiao({
          lat: Number(latitude),
          lng: Number(longitude),
        });
      }
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const region = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          setRegiao(region);
        },
        () => {
          toast.warn("Não foi possível obter sua localização.");
        }
      );
    }

    setTimeout(() => {
      const bounds = mapRef.current?.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast(); // canto superior direito
        const sw = bounds.getSouthWest(); // canto inferior esquerdo

        const minLatitude = sw.lat();
        const maxLatitude = ne.lat();
        const minLongitude = sw.lng();
        const maxLongitude = ne.lng();

        // Envie isso ao back-end ou filtre localmente
        const regiaoMax = {
          minLatitude,
          maxLatitude,
          minLongitude,
          maxLongitude,
        };
        setRegiaoMax(regiaoMax);
        searchPartners(regiaoMax, keywords, categoriesSearch.join(","));
      }
    }, 500);

    loadingCategories();
  }, []);

  useEffect(() => {
    if (regiaoMax) {
      if (zoom >= 13) {
        searchPartners(regiaoMax, keywordsSearch, categoriesSearch.join(","));
      } else {
        setPartners([]);
      }
    }
  }, [regiaoMax, zoom, keywordsSearch, categoriesSearch]);

  async function loadingCategories() {
    await api
      .get(`/categories`)
      .then((response) => {
        setCategories(response.data);
      })
      .catch((error) => {
        toast.warning("Ocorreu um erro ao carregar categorias");
      });
  }

  async function searchPartners(regiao, keywords, categories) {
    setIsLoading(true);
    await api
      .get(
        `/maps/partners?minLatitude=${regiao.minLatitude}&maxLatitude=${
          regiao.maxLatitude
        }&minLongitude=${regiao.minLongitude}&maxLongitude=${
          regiao.maxLongitude
        }&keywords=${keywords}${
          categories.length ? `&categories=${categories}` : ""
        }`
      )
      .then((response) => {
        setPartners(response.data);
      })
      .catch((error) => {
        toast.error("Ocorreu um erro, verifique sua conexão e tente novamente");
      });
    setIsLoading(false);
  }

  const onLoad = (autocomplete) => {
    setAutocomplete(autocomplete);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const newCenter = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setRegiao(newCenter);
        mapRef.current?.setZoom(14);
      }
    } else {
      console.log("O Autocomplete não está carregado ainda!");
    }
  };

  const handleToggleCategory = useCallback((categoryId) => {
    setCategoriesFilter((prev) => {
      const strId = String(categoryId);
      if (prev.includes(strId)) {
        return prev.filter((id) => id !== strId);
      }
      return [...prev, strId];
    });
  }, []);

  const getPartnerCategoryColor = useCallback(
    (marker) => {
      if (marker.color) {
        return marker.color;
      }
      if (marker.category_id && categoryColorMap[String(marker.category_id)]) {
        return categoryColorMap[String(marker.category_id)];
      }
      return DEFAULT_CATEGORY_COLOR;
    },
    [categoryColorMap]
  );

  if (loadError) return "Erro ao carregar o mapa";
  if (!isLoaded) return "Carregando Mapa...";

  const partnerColor = partner?.color
    ? partner.color
    : partner?.category_id
    ? categoryColorMap[String(partner.category_id)] || DEFAULT_CATEGORY_COLOR
    : DEFAULT_CATEGORY_COLOR;

  return (
    <Container>
      <SearchContainer
        style={{
          position: "absolute",
          top: 15,
          left: 15,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          width: `calc(100% - 30px)`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "row",
            width: "100%",
            maxWidth: 420,
          }}
        >
          <Autocomplete
            className="input"
            onLoad={onLoad}
            onPlaceChanged={onPlaceChanged}
          >
            <Input
              type="text"
              placeholder="Buscar endereço..."
              style={{
                boxSizing: `border-box`,
                border: `1px solid #ddd`,
                marginRight: 10,
                width: `100%`,
                maxWidth: 300,
                height: `38px`,
                padding: `0 14px`,
                borderRadius: `10px`,
                boxShadow: `0 2px 10px rgba(0, 0, 0, 0.08)`,
                fontSize: `14px`,
                fontFamily: SYSTEM_FONT,
                display: "block",
                textOverflow: `ellipsis`,
                backgroundColor: "#fff",
              }}
            />
          </Autocomplete>
          {filterQ != "false" && (
            <Button
              type="primary"
              style={{
                boxSizing: `border-box`,
                border: `none`,
                boxShadow: `0 2px 10px rgba(0, 0, 0, 0.12)`,
                marginLeft: 6,
                height: 38,
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontFamily: SYSTEM_FONT,
                fontWeight: 600,
                fontSize: 13,
                backgroundColor: DEFAULT_CATEGORY_COLOR,
              }}
              onClick={() => {
                setKeywords(keywordsSearch);
                setCategoriesFilter([...categoriesSearch]);
                setVisibleModalFilter(true);
              }}
            >
              <MdOutlineFilterList size={18} />
              Filtros
              {categoriesSearch.length > 0 && (
                <span
                  style={{
                    backgroundColor: "rgba(255,255,255,0.3)",
                    padding: "2px 7px",
                    borderRadius: 20,
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {categoriesSearch.length}
                </span>
              )}
            </Button>
          )}
        </div>

        {zoom < 13 ? (
          <WarningTag>Aproxime o mapa para fazer uma busca</WarningTag>
        ) : (
          <TotalPartners
            style={{
              backgroundColor: "rgba(255,255,255,0.95)",
              borderRadius: 50,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            {partners.length
              ? `${
                  partners.length +
                  ` parceiro${partners.length > 1 ? "s" : ""} encontrado${
                    partners.length > 1 ? "s" : ""
                  }`
                }`
              : "Nenhum parceiro encontrado"}
          </TotalPartners>
        )}
      </SearchContainer>

      {/* Modal de Filtros */}
      <Modal
        title="Filtros"
        width={500}
        open={visibleModalFilter}
        cancelText="FECHAR"
        okText="APLICAR FILTROS"
        onCancel={() => {
          setVisibleModalFilter(false);
        }}
        onOk={() => {
          setCategoriesSearch([...categoriesFilter]);
          setKeywordsSearch(keywords);
          setVisibleModalFilter(false);
        }}
        okButtonProps={{
          style: {
            backgroundColor: DEFAULT_CATEGORY_COLOR,
            borderColor: DEFAULT_CATEGORY_COLOR,
            borderRadius: 8,
            fontFamily: SYSTEM_FONT,
            fontWeight: 600,
            height: 38,
          },
        }}
        cancelButtonProps={{
          style: {
            borderRadius: 8,
            fontFamily: SYSTEM_FONT,
            height: 38,
          },
        }}
      >
        <FilterModalContent>
          <div className="filter-section" style={{ marginTop: 16 }}>
            <div className="filter-label">
              Buscar por
            </div>
            <Input
              value={keywords}
              placeholder="Nome ou palavras-chave..."
              style={{
                height: 42,
                fontSize: 14,
                borderRadius: 10,
                backgroundColor: "#fff",
                border: "1.5px solid #e0e0e0",
                fontFamily: SYSTEM_FONT,
              }}
              onChange={(e) => {
                setKeywords(e.target.value.replace(/ /g, "").toLowerCase());
              }}
              allowClear
            />
          </div>

          <div className="filter-section">
            <div className="filter-label">
              Categorias
              {categoriesFilter.length > 0 && (
                <span className="active-filters-count">
                  {categoriesFilter.length}
                </span>
              )}
            </div>

            <div className="filter-chips">
              <CategoryChip
                $selected={!categoriesFilter.length}
                $color={DEFAULT_CATEGORY_COLOR}
                onClick={() => {
                  setCategoriesFilter([]);
                }}
              >
                TODAS
              </CategoryChip>
              {categories.map((item) => {
                if (
                  !categoriesFilterQ ||
                  categoriesFilterQ.includes(item.id)
                ) {
                  const color = item.color || DEFAULT_CATEGORY_COLOR;
                  const isSelected = categoriesFilter.includes(String(item.id));
                  return (
                    <CategoryChip
                      key={item.id}
                      $selected={isSelected}
                      $color={color}
                      onClick={() => handleToggleCategory(item.id)}
                    >
                      <CategoryDot $color={isSelected ? "#fff" : color} />
                      {item.name}
                    </CategoryChip>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </FilterModalContent>
      </Modal>

      {/* Modal do Parceiro */}
      <Modal
        title="Parceiro"
        width={500}
        open={visibleModal}
        cancelText={"FECHAR"}
        okButtonProps={{
          style: {
            display: "none",
          },
        }}
        cancelButtonProps={{
          style: {
            display: "none",
          },
        }}
        onCancel={() => {
          setVisibleModal(false);
          setPartner(null);
        }}
      >
        <div
          style={{
            width: "100%",
            margin: "25px 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {partner && (
            <Photo
              src={partner?.photo_url || defaultUserImage}
              style={{
                border: `3px solid ${partnerColor}`,
                boxShadow: `0 4px 16px ${hexToRgba(partnerColor, 0.2)}`,
              }}
            />
          )}

          <Name numberOfLines={2}>{partner?.label}</Name>

          {partner?.category_name && (
            <TooltipCategoryBadge
              $color={partnerColor}
              style={{
                fontSize: 11,
                padding: "4px 14px",
                marginBottom: 8,
              }}
            >
              {partner.category_name}
            </TooltipCategoryBadge>
          )}

          <Description>{partner?.description}</Description>
          <Address>
            {partner?.street}, {partner?.number} - {partner?.neighborhood},{" "}
            {partner?.city} - {partner?.state}, {partner?.postal_code}
          </Address>
          <div style={{ display: "flex", flexDirection: "row" }}>
            {partner?.whatsapp && (
              <Redes
                style={{ width: 35, height: 35 }}
                onClick={() => {
                  window.open(
                    "https://api.whatsapp.com/send?phone=" + partner?.whatsapp,
                    "_blank"
                  );
                }}
              >
                <RedesImage
                  style={{ width: 35, height: 35 }}
                  src={redesSociais["Whatsapp"].icon}
                />
              </Redes>
            )}
            {partner?.whatsapp && (
              <Redes
                style={{ width: 35, height: 35, marginLeft: 0 }}
                onClick={() => {
                  window.open("tel:" + partner?.whatsapp, "_blank");
                }}
              >
                <RedesImage
                  style={{ width: 35, height: 35 }}
                  src={redesSociais["Telefone"].icon}
                />
              </Redes>
            )}
            {partner?.instagram && (
              <Redes
                style={{
                  width: 35,
                  height: 35,
                  marginLeft: 0,
                  backgroundColor: "#aaa",
                }}
                onClick={() => {
                  window.open(partner?.instagram, "_blank");
                }}
              >
                <RedesImage
                  style={{ width: 33, height: 33, marginTop: 1 }}
                  src={redesSociais["Instagram"].icon}
                />
              </Redes>
            )}
            <Redes
              style={{
                width: 35,
                height: 35,
                marginLeft: 0,
                backgroundColor: "#aaa",
              }}
              onClick={() => {
                window.open(
                  `http://maps.google.com/maps?q=loc:${partner.latitude},${partner.longitude}`,
                  "_blank"
                );
              }}
            >
              <RedesImage
                style={{ width: 33, height: 33, marginTop: 1 }}
                src={redesSociais["Maps"].icon}
              />
            </Redes>
          </div>
          {partner?.url && (
            <Button
              type="primary"
              style={{
                height: 42,
                width: "100%",
                fontSize: 15,
                marginTop: 15,
                borderRadius: 10,
                fontFamily: SYSTEM_FONT,
                fontWeight: 600,
                backgroundColor: partnerColor,
                borderColor: partnerColor,
                boxShadow: `0 3px 12px ${hexToRgba(partnerColor, 0.25)}`,
              }}
              onClick={() => {
                window.open(partner.url, "_blank");
              }}
            >
              Mais informações
            </Button>
          )}
        </div>
      </Modal>

      {isLoading && (
        <Load>
          <Spin />
        </Load>
      )}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={zoom}
        ref={mapRef}
        onLoad={(map) => (mapRef.current = map)}
        onIdle={() => {
          const newZoom = mapRef.current?.getZoom();
          if (newZoom !== undefined) {
            setZoom(newZoom);
          }

          const bounds = mapRef.current?.getBounds();
          if (bounds) {
            const ne = bounds.getNorthEast(); // canto superior direito
            const sw = bounds.getSouthWest(); // canto inferior esquerdo

            const minLatitude = sw.lat();
            const maxLatitude = ne.lat();
            const minLongitude = sw.lng();
            const maxLongitude = ne.lng();

            const regiao = {
              minLatitude,
              maxLatitude,
              minLongitude,
              maxLongitude,
            };
            setRegiaoMax(regiao);
          }
        }}
        options={{
          fullscreenControl: false,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          scaleControl: false,
          rotateControl: false,
          clickableIcons: false,
          disableDefaultUI: true,
        }}
        center={regiao}
        onClick={(event) => {
          // Lógica para adicionar marcadores foi removida para focar nos filtros
        }}
      >
        {partners.map((marker) => (
          <PartnerMarker
            key={marker.id}
            marker={marker}
            onClick={handleMarkerClick}
            defaultUserImage={defaultUserImage}
            categoryColor={getPartnerCategoryColor(marker)}
            pinSize={pinSize}
          />
        ))}
      </GoogleMap>
    </Container>
  );
}
